/**
 * Mode Selection Step Component
 * 
 * First step in Auto Fleet Generator - allows user to choose between
 * Trip Layer Analysis (analyzing existing trips) or Manual Input (specifying parameters directly)
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Alert,
  Paper,
  Chip,
  Divider,
  Stack,
  Checkbox,
} from '@mui/material';

import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip as ChartTooltip, Legend } from 'chart.js';

import { useFeatureFlags } from '../../../../services/featureFlags/useFeatureFlags';
import { ModeSelectionStepProps, TripAnalysisResult } from './types';
import {
  getTripLayersFromContext,
  analyzeTripsLayer,
  validateTripLayer,
  getTripLayerSummary,
} from './tripAnalysis';
import { useFixedRoutes } from '../../../../context/FixedRoutesContext';
import { validateRoutes } from '../../../../utils/fixedRoutes/routeValidator';
import { calculateRouteLoopDuration } from '../../../../utils/fixedRoutes/vehicleCalculator';
import { logEvent } from '../../../../services/analytics';

import { Icon } from '@liftango/ui-icons';
import { ICON_FALLBACKS } from '../../../../utils/iconFallbacks';
// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, ChartTooltip, Legend);

const ModeSelectionStep: React.FC<ModeSelectionStepProps> = ({
  config,
  onConfigChange,
  layers,
  onAnalysisComplete,
}) => {
  const { t } = useTranslation('mapWizards');
  const { routes, trafficMultiplier } = useFixedRoutes();
  const { isFeatureRuntimeEnabled } = useFeatureFlags();
  const fixedRouteFleetEnabled = isFeatureRuntimeEnabled('auto_generate_fixed_route_fleet');
  const [analysisResult, setAnalysisResult] = useState<TripAnalysisResult | null>(null);
  const [analysisError, setAnalysisError] = useState<string | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  
  // Route selection state
  const [selectedRouteIds, setSelectedRouteIds] = useState<string[]>(
    config.selectedRouteIds || []
  );
  const [selectAll, setSelectAll] = useState(false);
  const [selectedLayerId, setSelectedLayerId] = useState<string>(
    config.selectedLayerId || ''
  );

  // Get available trip layers
  const tripLayers = getTripLayersFromContext(layers || []);
  const hasTripLayers = tripLayers.length > 0;

  // Validate all routes
  const routeValidations = useMemo(() => {
    if (routes.length === 0) return [];
    return validateRoutes(routes, trafficMultiplier);
  }, [routes, trafficMultiplier]);

  // Calculate loop duration for circulators (for display)
  const routeLoopDurations = useMemo(() => {
    const durations = new Map<string, number>();
    routes.forEach((route) => {
      if (route.isCirculator) {
        try {
          const routeTrafficMultiplier = route.trafficMultiplier ?? trafficMultiplier;
          const duration = calculateRouteLoopDuration(route, routeTrafficMultiplier);
          durations.set(route.id, duration);
        } catch (error) {
          // Handle error silently - will be shown in validation
        }
      }
    });
    return durations;
  }, [routes, trafficMultiplier]);

  // Get valid routes (routes that pass validation)
  const validRoutes = useMemo(() => {
    return routes.filter((route, index) => routeValidations[index]?.isValid ?? false);
  }, [routes, routeValidations]);

  // Format time duration for display
  const formatDuration = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  // Handle route selection change
  const handleRouteToggle = (routeId: string) => {
    setSelectedRouteIds((prev) => {
      if (prev.includes(routeId)) {
        const updated = prev.filter((id) => id !== routeId);
        onConfigChange({ selectedRouteIds: updated });
        logEvent({
          category: 'AutoFleetGenerator',
          action: 'RouteDeselected',
          label: routeId,
        });
        return updated;
      } else {
        const updated = [...prev, routeId];
        onConfigChange({ selectedRouteIds: updated });
        logEvent({
          category: 'AutoFleetGenerator',
          action: 'RouteSelected',
          label: routeId,
        });
        return updated;
      }
    });
  };

  // Handle select all toggle
  const handleSelectAllToggle = () => {
    if (selectAll) {
      setSelectedRouteIds([]);
      onConfigChange({ selectedRouteIds: [] });
      logEvent({
        category: 'AutoFleetGenerator',
        action: 'AllRoutesDeselected',
      });
    } else {
      const allValidRouteIds = validRoutes.map((route) => route.id);
      setSelectedRouteIds(allValidRouteIds);
      onConfigChange({ selectedRouteIds: allValidRouteIds });
      logEvent({
        category: 'AutoFleetGenerator',
        action: 'AllRoutesSelected',
        routes_count: allValidRouteIds.length,
      });
    }
    setSelectAll(!selectAll);
  };

  // Handle mixed service layer selection
  const handleMixedLayerChange = (event: any) => {
    const layerId = event.target.value;
    setSelectedLayerId(layerId);
    onConfigChange({ selectedLayerId: layerId });
    
    logEvent({
      category: 'AutoFleetGenerator',
      action: 'TripLayerSelected',
      label: 'Mixed Service',
      layer_id: layerId,
    });
  };

  // Update select all state when selection changes
  useEffect(() => {
    if (validRoutes.length === 0) {
      setSelectAll(false);
    } else {
      setSelectAll(
        selectedRouteIds.length === validRoutes.length &&
        validRoutes.every((route) => selectedRouteIds.includes(route.id))
      );
    }
  }, [selectedRouteIds, validRoutes]);

  // Sync selectedRouteIds with config
  useEffect(() => {
    if (config.selectedRouteIds) {
      setSelectedRouteIds(config.selectedRouteIds);
    }
  }, [config.selectedRouteIds]);

  // Sync selectedLayerId with config
  useEffect(() => {
    if (config.selectedLayerId !== undefined) {
      setSelectedLayerId(config.selectedLayerId);
    }
  }, [config.selectedLayerId]);

  // Handle mode change
  const handleModeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newMode = event.target.value as 'trip-layer' | 'manual' | 'fixed-routes' | 'mixed';
    
    onConfigChange({ mode: newMode });
    
    // Clear analysis when switching modes
    setAnalysisResult(null);
    setAnalysisError(null);

    // Track mode selection
    logEvent({
      category: 'AutoFleetGenerator',
      action: 'ModeSelected',
      label: newMode === 'trip-layer' ? 'Trip Layer Analysis' : 
             newMode === 'fixed-routes' ? 'Fixed Routes' : 
             newMode === 'mixed' ? 'Mixed Service' :
             'Manual Input',
      mode: newMode,
    });
  };

  // Handle layer selection and analysis
  const handleLayerChange = (event: any) => {
    const layerId = event.target.value;
    onConfigChange({ selectedLayerId: layerId });
    
    // Analyze the selected layer
    const selectedLayer = layers.find(l => l.layerId.toString() === layerId);
    if (selectedLayer) {
      analyzeLayer(selectedLayer);
    }
  };

  // Analyze trip layer
  const analyzeLayer = (layer: any) => {
    setIsAnalyzing(true);
    setAnalysisError(null);
    
    try {
      // Validate layer first
      const validation = validateTripLayer(layer);
      if (!validation.isValid) {
        setAnalysisError(validation.error || t('autoFleetGen.modeSelection.tripLayer.invalidLayer'));
        setAnalysisResult(null);
        setIsAnalyzing(false);
        
        logEvent({
          category: 'AutoFleetGenerator',
          action: 'TripLayerAnalysisFailed',
          label: validation.error || 'Unknown error',
          layer_id: layer.layerId,
        });
        return;
      }

      // Perform analysis
      const result = analyzeTripsLayer(layer, config.tripsPerVehiclePerHour);
      setAnalysisResult(result);
      
      // Update config with analysis results
      onConfigChange({
        serviceStartTime: result.suggestedServiceStart,
        serviceEndTime: result.suggestedServiceEnd,
        manualVehicleCount: result.calculatedVehicles,
      });

      // Notify parent component
      if (onAnalysisComplete) {
        onAnalysisComplete(result);
      }

      // Track successful analysis
      logEvent({
        category: 'AutoFleetGenerator',
        action: 'TripLayerAnalyzed',
        label: layer.layerName || 'Unnamed Layer',
        layer_id: layer.layerId,
        total_trips: result.totalTrips,
        calculated_vehicles: result.calculatedVehicles,
        peak_demand: result.peakDemand,
      });
    } catch (error) {
      console.error('Error analyzing trip layer:', error);
      setAnalysisError(t('autoFleetGen.modeSelection.tripLayer.analysisError'));
      setAnalysisResult(null);
      
      logEvent({
        category: 'AutoFleetGenerator',
        action: 'TripLayerAnalysisError',
        label: error instanceof Error ? error.message : 'Unknown error',
        layer_id: layer.layerId,
      });
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Auto-analyze when layer is selected
  useEffect(() => {
    if (config.mode === 'trip-layer' && config.selectedLayerId && !analysisResult && !isAnalyzing) {
      const selectedLayer = layers.find(l => l.layerId.toString() === config.selectedLayerId);
      if (selectedLayer) {
        console.log('Auto-analyzing selected layer:', selectedLayer.layerName);
        analyzeLayer(selectedLayer);
      }
    }
  }, [config.mode, config.selectedLayerId, analysisResult, isAnalyzing, layers]);

  // Auto-analyze if only one trip layer exists
  useEffect(() => {
    if (
      config.mode === 'trip-layer' &&
      tripLayers.length === 1 &&
      !config.selectedLayerId &&
      !analysisResult
    ) {
      const singleLayer = tripLayers[0];
      onConfigChange({ selectedLayerId: singleLayer.layerId.toString() });
      analyzeLayer(singleLayer);
    }
  }, [config.mode, tripLayers.length]);

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        {t('autoFleetGen.modeSelection.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t('autoFleetGen.modeSelection.description')}
      </Typography>

      {/* Mode Selection Radio Buttons */}
      <FormControl component="fieldset" fullWidth sx={{ mb: 3 }}>
        <RadioGroup value={config.mode} onChange={handleModeChange}>
          <Paper
            sx={{
              p: 2,
              mb: 2,
              border: config.mode === 'trip-layer' ? '2px solid #1976d2' : '1px solid #e0e0e0',
              cursor: 'pointer',
              '&:hover': { bgcolor: '#f5f5f5' },
            }}
            onClick={() => handleModeChange({ target: { value: 'trip-layer' } } as any)}
          >
            <FormControlLabel
              value="trip-layer"
              control={<Radio />}
              label={
                <Box display="flex" alignItems="center" gap={1}>
                  <Icon size={20} d={ICON_FALLBACKS.Layers} />
                  <Box>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {t('autoFleetGen.modeSelection.options.tripLayer.title')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('autoFleetGen.modeSelection.options.tripLayer.description')}
                    </Typography>
                  </Box>
                </Box>
              }
              sx={{ m: 0, width: '100%' }}
            />
          </Paper>

          <Paper
            sx={{
              p: 2,
              border: config.mode === 'manual' ? '2px solid #1976d2' : '1px solid #e0e0e0',
              cursor: 'pointer',
              '&:hover': { bgcolor: '#f5f5f5' },
            }}
            onClick={() => handleModeChange({ target: { value: 'manual' } } as any)}
          >
            <FormControlLabel
              value="manual"
              control={<Radio />}
              label={
                <Box display="flex" alignItems="center" gap={1}>
                  <Icon size={20} name="pen-to-square" />
                  <Box>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {t('autoFleetGen.modeSelection.options.manual.title')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('autoFleetGen.modeSelection.options.manual.description')}
                    </Typography>
                  </Box>
                </Box>
              }
              sx={{ m: 0, width: '100%' }}
            />
          </Paper>

          {fixedRouteFleetEnabled && (
            <Paper
              sx={{
                p: 2,
                mt: 2,
                border: config.mode === 'fixed-routes' ? '2px solid #1976d2' : '1px solid #e0e0e0',
                cursor: 'pointer',
                '&:hover': { bgcolor: '#f5f5f5' },
              }}
              onClick={() => handleModeChange({ target: { value: 'fixed-routes' } } as any)}
            >
              <FormControlLabel
                value="fixed-routes"
                control={<Radio />}
                label={
                  <Box display="flex" alignItems="center" gap={1}>
                    <Icon size={20} name="bus" />
                    <Box>
                      <Typography variant="subtitle1" fontWeight={600}>
                        {t('autoFleetGen.modeSelection.options.fixedRoutes.title')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t('autoFleetGen.modeSelection.options.fixedRoutes.description')}
                      </Typography>
                    </Box>
                  </Box>
                }
                sx={{ m: 0, width: '100%' }}
              />
            </Paper>
          )}

          {fixedRouteFleetEnabled && (
            <Paper
              sx={{
                p: 2,
                mt: 2,
                border: config.mode === 'mixed' ? '2px solid #1976d2' : '1px solid #e0e0e0',
                cursor: 'pointer',
                '&:hover': { bgcolor: '#f5f5f5' },
              }}
              onClick={() => handleModeChange({ target: { value: 'mixed' } } as any)}
            >
              <FormControlLabel
                value="mixed"
                control={<Radio />}
                label={
                  <Box display="flex" alignItems="center" gap={1}>
                    <Icon size={20} d={ICON_FALLBACKS.SwapHoriz} />
                    <Box>
                      <Typography variant="subtitle1" fontWeight={600}>
                        {t('autoFleetGen.modeSelection.options.mixed.title')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t('autoFleetGen.modeSelection.options.mixed.description')}
                      </Typography>
                    </Box>
                  </Box>
                }
                sx={{ m: 0, width: '100%' }}
              />
            </Paper>
          )}
        </RadioGroup>
      </FormControl>

      <Divider sx={{ my: 3 }} />

      {/* Trip Layer Analysis Mode */}
      {config.mode === 'trip-layer' && (
        <Box>
          {!hasTripLayers ? (
            <Alert severity="error" icon={<Icon size={20} d={ICON_FALLBACKS.Layers} />}>
              <Typography variant="subtitle2" gutterBottom>
                {t('autoFleetGen.modeSelection.tripLayer.noLayersTitle')}
              </Typography>
              <Typography variant="body2">
                {t('autoFleetGen.modeSelection.tripLayer.noLayersBody')}
              </Typography>
            </Alert>
          ) : (
            <>
              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel>{t('autoFleetGen.modeSelection.tripLayer.selectLayerLabel')}</InputLabel>
                <Select
                  value={config.selectedLayerId || ''}
                  onChange={handleLayerChange}
                  label={t('autoFleetGen.modeSelection.tripLayer.selectLayerLabel')}
                  MenuProps={{ sx: { zIndex: 2600 } }}
                >
                  {tripLayers.map((layer) => {
                    const summary = getTripLayerSummary(layer);
                    return (
                      <MenuItem key={layer.layerId} value={layer.layerId.toString()}>
                        <Box>
                          <Typography variant="body1">
                            {layer.layerName || `Layer ${layer.layerId}`}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {t('autoFleetGen.modeSelection.tripLayer.tripsCount', { count: summary.tripCount })}
                            {summary.tripsWithTime < summary.tripCount &&
                              ` ${t('autoFleetGen.modeSelection.tripLayer.tripsWithTime', { value: summary.tripsWithTime })}`}
                          </Typography>
                        </Box>
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>

              {/* Analysis Error Display */}
              {analysisError && (
                <Alert severity="warning" sx={{ mb: 3 }}>
                  {analysisError}
                </Alert>
              )}

              {/* Analysis Results Display */}
              {isAnalyzing && (
                <Box textAlign="center" py={3}>
                  <Typography variant="body2" color="text.secondary">
                    {t('autoFleetGen.modeSelection.tripLayer.analyzing')}
                  </Typography>
                </Box>
              )}

              {analysisResult && !isAnalyzing && (
                <Paper sx={{ p: 3, bgcolor: '#e3f2fd', border: '2px solid #1976d2' }}>
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <Icon size={20} d={ICON_FALLBACKS.Assessment} />
                    <Typography variant="h6" color="primary">✓ {t('autoFleetGen.modeSelection.tripLayer.results')}</Typography>
                  </Box>

                  <Stack spacing={2}>
                    {/* Total Trips and Calculated Vehicles */}
                    <Box display="flex" gap={2}>
                      <Paper sx={{ p: 2, textAlign: 'center', flex: 1 }}>
                        <Icon size={32} d={ICON_FALLBACKS.Layers} style={{ marginBottom: 8 }} />
                        <Typography variant="h4" color="primary">
                          {analysisResult.totalTrips}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {t('autoFleetGen.modeSelection.tripLayer.totalTrips')}
                        </Typography>
                      </Paper>

                      <Paper sx={{ p: 2, textAlign: 'center', flex: 1 }}>
                        <Icon size={32} name="bus" style={{ marginBottom: 8 }} />
                        <Typography variant="h4" color="secondary">
                          {analysisResult.calculatedVehicles}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {t('autoFleetGen.modeSelection.tripLayer.vehiclesNeeded')}
                        </Typography>
                      </Paper>
                    </Box>

                    {/* Service Hours */}
                    <Paper sx={{ p: 2 }}>
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <Icon size={20} name="clock" />
                        <Typography variant="subtitle2">{t('autoFleetGen.modeSelection.tripLayer.serviceHours')}</Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between">
                        <Chip
                          label={t('autoFleetGen.modeSelection.tripLayer.startChip', { value: analysisResult.suggestedServiceStart })}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                        <Chip
                          label={t('autoFleetGen.modeSelection.tripLayer.endChip', { value: analysisResult.suggestedServiceEnd })}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </Box>
                    </Paper>

                    {/* Peak Demand */}
                    <Paper sx={{ p: 2 }}>
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <Icon size={20} d={ICON_FALLBACKS.TrendingUp} />
                        <Typography variant="subtitle2">{t('autoFleetGen.modeSelection.tripLayer.peakDemandHeader')}</Typography>
                      </Box>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>{t('autoFleetGen.modeSelection.tripLayer.peakDemandSummary', { count: analysisResult.peakDemand, hour: analysisResult.peakHour.toString().padStart(2, '0') })}</strong>
                      </Typography>
                      {analysisResult.detectedPeakPeriods && analysisResult.detectedPeakPeriods.length > 0 && (
                        <Box mt={1}>
                          <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                            {t('autoFleetGen.modeSelection.tripLayer.detectedPeakPeriods', { minPercent: 70 })}
                          </Typography>
                          <Box display="flex" gap={0.5} flexWrap="wrap">
                            {analysisResult.detectedPeakPeriods.map((peak, idx) => (
                              <Chip
                                key={idx}
                                label={`${peak.start} - ${peak.end}`}
                                size="small"
                                color="warning"
                                variant="outlined"
                              />
                            ))}
                          </Box>
                        </Box>
                      )}
                    </Paper>

                    {/* Hourly Demand Distribution Chart */}
                    <Paper sx={{ p: 2 }}>
                      <Box display="flex" alignItems="center" gap={1} mb={2}>
                        <Icon size={20} d={ICON_FALLBACKS.Assessment} />
                        <Typography variant="subtitle2">{t('autoFleetGen.modeSelection.tripLayer.hourlyDemandDistribution')}</Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontSize: '0.875rem' }}>
                        {t('autoFleetGen.modeSelection.tripLayer.hourlyDemandDescription')}
                      </Typography>
                      <Bar
                        data={{
                          labels: Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0') + ':00'),
                          datasets: [
                            {
                              label: t('autoFleetGen.modeSelection.tripLayer.chart.tripsPerHour'),
                              data: Array.from({ length: 24 }, (_, i) => analysisResult.hourlyDemand[i] || 0),
                              backgroundColor: Array.from({ length: 24 }, (_, i) =>
                                i === analysisResult.peakHour ? 'rgba(25, 118, 210, 0.8)' : 'rgba(144, 202, 249, 0.6)'
                              ),
                              borderColor: Array.from({ length: 24 }, (_, i) =>
                                i === analysisResult.peakHour ? 'rgba(25, 118, 210, 1)' : 'rgba(144, 202, 249, 1)'
                              ),
                              borderWidth: 1,
                            },
                          ],
                        }}
                        options={{
                          responsive: true,
                          maintainAspectRatio: true,
                          scales: {
                            x: {
                              grid: { display: false },
                              ticks: {
                                callback: function(value, index) {
                                  // Show every 2 hours for readability
                                  return index % 2 === 0 ? this.getLabelForValue(value as number) : '';
                                },
                              },
                            },
                            y: {
                              grid: { color: 'rgba(0, 0, 0, 0.05)' },
                              beginAtZero: true,
                              ticks: {
                                precision: 0,
                              },
                            },
                          },
                          plugins: {
                            legend: { display: false },
                            tooltip: {
                              callbacks: {
                                label: (context) => t('autoFleetGen.modeSelection.tripLayer.chart.tripsTooltip', { count: context.parsed.y as number }),
                              },
                            },
                          },
                        }}
                        height={100}
                      />
                    </Paper>
                  </Stack>

                  <Alert severity="info" sx={{ mt: 2 }}>
                    <Typography variant="body2">
                      {t('autoFleetGen.modeSelection.tripLayer.defaultsHint')}
                    </Typography>
                  </Alert>
                </Paper>
              )}
            </>
          )}
        </Box>
      )}

      {/* Manual Input Mode */}
      {config.mode === 'manual' && (
        <Box>
          <Typography variant="subtitle1" gutterBottom>
            {t('autoFleetGen.modeSelection.manual.subtitle')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('autoFleetGen.modeSelection.manual.description')}
          </Typography>

          <Stack spacing={2}>
            <Box display="flex" gap={2}>
              <TextField
                label={t('autoFleetGen.modeSelection.manual.serviceStart')}
                type="time"
                fullWidth
                value={config.serviceStartTime}
                onChange={(e) => onConfigChange({ serviceStartTime: e.target.value })}
                InputLabelProps={{ shrink: true }}
                inputProps={{ lang: 'en-GB' }}
                helperText={t('autoFleetGen.modeSelection.manual.serviceStartHelper')}
              />
              <TextField
                label={t('autoFleetGen.modeSelection.manual.serviceEnd')}
                type="time"
                fullWidth
                value={config.serviceEndTime}
                onChange={(e) => onConfigChange({ serviceEndTime: e.target.value })}
                InputLabelProps={{ shrink: true }}
                inputProps={{ lang: 'en-GB' }}
                helperText={t('autoFleetGen.modeSelection.manual.serviceEndHelper')}
              />
            </Box>
            <TextField
              label={t('autoFleetGen.modeSelection.manual.numberOfVehicles')}
              type="number"
              fullWidth
              value={config.manualVehicleCount || ''}
              onChange={(e) =>
                onConfigChange({ manualVehicleCount: parseInt(e.target.value, 10) || 1 })
              }
              inputProps={{ min: 1 }}
              helperText={t('autoFleetGen.modeSelection.manual.numberOfVehiclesHelper')}
            />
          </Stack>

          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              {t('autoFleetGen.modeSelection.manual.extraParamsHint')}
            </Typography>
          </Alert>
        </Box>
      )}

      {/* Fixed Routes Mode */}
      {config.mode === 'fixed-routes' && (
        <Box>
          <Typography variant="subtitle1" gutterBottom>
            {t('autoFleetGen.modeSelection.fixedRoutes.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('autoFleetGen.modeSelection.fixedRoutes.description')}
          </Typography>

          {routes.length === 0 ? (
            <Alert severity="info" icon={<Icon size={20} name="bus" />}>
              <Typography variant="subtitle2" gutterBottom>
                {t('autoFleetGen.modeSelection.fixedRoutes.noRoutesTitle')}
              </Typography>
              <Typography variant="body2">
                {t('autoFleetGen.modeSelection.fixedRoutes.noRoutesBody')}
              </Typography>
            </Alert>
          ) : (
            <>
              {/* Select All Checkbox */}
              {validRoutes.length > 0 && (
                <Paper sx={{ p: 1.5, mb: 2, bgcolor: '#f5f5f5' }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selectAll}
                        onChange={handleSelectAllToggle}
                        indeterminate={
                          selectedRouteIds.length > 0 && selectedRouteIds.length < validRoutes.length
                        }
                      />
                    }
                    label={
                      <Typography variant="subtitle2" fontWeight={600}>
                        {t(
                          validRoutes.length === 1
                            ? 'autoFleetGen.modeSelection.fixedRoutes.selectAllOne'
                            : 'autoFleetGen.modeSelection.fixedRoutes.selectAllOther',
                          { total: validRoutes.length }
                        )}
                      </Typography>
                    }
                  />
                </Paper>
              )}

              {/* Routes List */}
              <Stack spacing={1}>
                {routes.map((route, index) => {
                  const validation = routeValidations[index];
                  const isValid = validation?.isValid ?? false;
                  const isSelected = selectedRouteIds.includes(route.id);
                  const loopDuration = routeLoopDurations.get(route.id);
                  const routeType = route.isCirculator
                    ? t('autoFleetGen.modeSelection.fixedRoutes.routeTypeCirculator')
                    : t('autoFleetGen.modeSelection.fixedRoutes.routeTypeNonCirculator');

                  return (
                    <Paper
                      key={route.id}
                      sx={{
                        p: 2,
                        border: isSelected
                          ? '2px solid #1976d2'
                          : isValid
                          ? '1px solid #e0e0e0'
                          : '1px solid #ff9800',
                        bgcolor: isSelected ? '#e3f2fd' : isValid ? 'white' : '#fff3e0',
                        cursor: isValid ? 'pointer' : 'not-allowed',
                        opacity: isValid ? 1 : 0.7,
                        '&:hover': isValid ? { bgcolor: isSelected ? '#e3f2fd' : '#f5f5f5' } : {},
                      }}
                      onClick={() => {
                        if (isValid) {
                          handleRouteToggle(route.id);
                        }
                      }}
                    >
                      <Box display="flex" alignItems="flex-start" gap={2}>
                        {/* Checkbox */}
                        <Checkbox
                          checked={isSelected}
                          onChange={() => {
                            if (isValid) {
                              handleRouteToggle(route.id);
                            }
                          }}
                          disabled={!isValid}
                          sx={{ mt: -1 }}
                        />

                        {/* Route Info */}
                        <Box flex={1}>
                          <Box display="flex" alignItems="center" gap={1} mb={1}>
                            <Icon size={20} name="bus" />
                            <Typography variant="subtitle1" fontWeight={600}>
                              {route.name || route.id}
                            </Typography>
                            <Chip
                              label={routeType}
                              size="small"
                              color={route.isCirculator ? 'primary' : 'default'}
                              variant="outlined"
                            />
                            {!isValid && (
                              <Chip
                                icon={<Icon size={20} name="triangle-exclamation" />}
                                label={t('autoFleetGen.modeSelection.fixedRoutes.invalidChip')}
                                size="small"
                                color="warning"
                              />
                            )}
                            {isValid && isSelected && (
                              <Chip
                                icon={<Icon size={20} name="circle-check" />}
                                label={t('autoFleetGen.modeSelection.fixedRoutes.selectedChip')}
                                size="small"
                                color="primary"
                              />
                            )}
                          </Box>

                          {/* Route Details */}
                          <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ mb: 1 }}>
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <Icon size={16} name="clock" style={{ color: '#52565e' }} />
                              <Typography variant="body2" color="text.secondary">
                                {route.startTime}
                                {route.endTime ? ` - ${route.endTime}` : ' - 22:00'}
                              </Typography>
                            </Box>
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <Icon size={16} name="clock" style={{ color: '#52565e' }} />
                              <Typography variant="body2" color="text.secondary">
                                {t('autoFleetGen.modeSelection.fixedRoutes.stopsCount', { total: route.stops?.length || 0 })}
                              </Typography>
                            </Box>
                            {route.isCirculator && loopDuration && (
                              <Typography variant="body2" color="text.secondary">
                                {t('autoFleetGen.modeSelection.fixedRoutes.loop', { value: formatDuration(loopDuration) })}
                              </Typography>
                            )}
                          </Stack>

                          {/* Validation Errors/Warnings */}
                          {validation && !validation.isValid && (
                            <Alert severity="error" icon={<Icon size={20} name="triangle-exclamation" />} sx={{ mt: 1 }}>
                              <Typography variant="caption" component="div">
                                {validation.errors.map((error, idx) => (
                                  <div key={idx}>• {error}</div>
                                ))}
                              </Typography>
                            </Alert>
                          )}
                          {validation?.warnings && validation.warnings.length > 0 && (
                            <Alert severity="warning" sx={{ mt: 1 }}>
                              <Typography variant="caption" component="div">
                                {validation.warnings.map((warning, idx) => (
                                  <div key={idx}>• {warning}</div>
                                ))}
                              </Typography>
                            </Alert>
                          )}
                        </Box>
                      </Box>
                    </Paper>
                  );
                })}
              </Stack>

              {/* Selection Summary */}
              {selectedRouteIds.length > 0 && (
                <Paper sx={{ p: 2, mt: 3, bgcolor: '#e3f2fd' }}>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Icon size={20} name="circle-info" />
                    <Typography variant="subtitle2" fontWeight={600} color="primary">
                      {t('autoFleetGen.modeSelection.fixedRoutes.summaryTitle')}
                    </Typography>
                  </Box>
                  <Typography variant="body2">
                    {t(
                      selectedRouteIds.length === 1
                        ? 'autoFleetGen.modeSelection.fixedRoutes.summaryOneSelected'
                        : 'autoFleetGen.modeSelection.fixedRoutes.summaryOtherSelected',
                      { total: selectedRouteIds.length }
                    )}
                    {validRoutes.length !== routes.length &&
                      t(
                        routes.length - validRoutes.length === 1
                          ? 'autoFleetGen.modeSelection.fixedRoutes.summaryExcludedOne'
                          : 'autoFleetGen.modeSelection.fixedRoutes.summaryExcludedOther',
                        { total: routes.length - validRoutes.length }
                      )}
                  </Typography>
                </Paper>
              )}

              {/* No Valid Routes Warning */}
              {validRoutes.length === 0 && routes.length > 0 && (
                <Alert severity="warning" sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    {t('autoFleetGen.modeSelection.fixedRoutes.noValidRoutesTitle')}
                  </Typography>
                  <Typography variant="body2">
                    {t('autoFleetGen.modeSelection.fixedRoutes.noValidRoutesBody')}
                  </Typography>
                </Alert>
              )}
            </>
          )}
        </Box>
      )}

      {/* Mixed Service Mode */}
      {config.mode === 'mixed' && (
        <Box>
          <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Icon size={20} name="bus" /> {t('autoFleetGen.modeSelection.mixed.title')}
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('autoFleetGen.modeSelection.mixed.description')}
          </Typography>

          <Stack spacing={3}>
            {/* Fixed Routes Selection */}
            <Paper elevation={1} sx={{ p: 2 }}>
              <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Icon name="bus" size={20} /> {t('autoFleetGen.modeSelection.mixed.fixedRoutesTitle')}
              </Typography>

              {validRoutes.length === 0 ? (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  {t('autoFleetGen.modeSelection.mixed.noValidRoutes')}
                </Alert>
              ) : (
                <>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selectAll}
                        onChange={handleSelectAllToggle}
                        disabled={validRoutes.length === 0}
                      />
                    }
                    label={t(
                      validRoutes.length === 1
                        ? 'autoFleetGen.modeSelection.mixed.selectAllOne'
                        : 'autoFleetGen.modeSelection.mixed.selectAllOther',
                      { total: validRoutes.length }
                    )}
                    sx={{ mb: 1 }}
                  />
                  
                  <Divider sx={{ my: 1 }} />
                  
                  <Box sx={{ maxHeight: '300px', overflowY: 'auto' }}>
                    {validRoutes.map((route) => {
                      const isSelected = selectedRouteIds.includes(route.id);
                      const validation = routeValidations[routes.indexOf(route)];
                      const loopDuration = routeLoopDurations.get(route.id);
                      
                      return (
                        <Paper
                          key={route.id}
                          elevation={isSelected ? 2 : 0}
                          sx={{
                            p: 1.5,
                            mb: 1,
                            border: isSelected ? '2px solid' : '1px solid',
                            borderColor: isSelected ? 'primary.main' : 'divider',
                            backgroundColor: isSelected ? 'action.selected' : 'background.paper',
                            cursor: 'pointer',
                            '&:hover': {
                              backgroundColor: 'action.hover',
                            },
                          }}
                          onClick={() => handleRouteToggle(route.id)}
                        >
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Checkbox
                              checked={isSelected}
                              onChange={() => handleRouteToggle(route.id)}
                              onClick={(e) => e.stopPropagation()}
                            />
                            
                            <Box sx={{ flex: 1 }}>
                              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                <Box>
                                  <Typography variant="body1" fontWeight={isSelected ? 'bold' : 'normal'}>
                                    {route.name || route.id}
                                  </Typography>
                                  {route.shortName && (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: -0.5 }}>
                                      {route.shortName}
                                    </Typography>
                                  )}
                                </Box>
                                
                                {route.isCirculator && (
                                  <Chip
                                    label={t('autoFleetGen.modeSelection.fixedRoutes.routeTypeCirculator')}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                  />
                                )}

                                {validation?.isValid && (
                                  <Chip
                                    icon={<Icon size={20} name="circle-check" />}
                                    label={t('autoFleetGen.modeSelection.mixed.validChip')}
                                    size="small"
                                    color="success"
                                    variant="outlined"
                                  />
                                )}

                                {loopDuration && (
                                  <Chip
                                    icon={<Icon size={20} name="clock" />}
                                    label={t('autoFleetGen.modeSelection.mixed.minutesShort', { value: Math.round(loopDuration) })}
                                    size="small"
                                    variant="outlined"
                                  />
                                )}
                              </Stack>
                              
                              {route.startTime && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                                  <Icon name="clock" size={20} />
                                  {t('autoFleetGen.modeSelection.mixed.routeTimeRange', { start: route.startTime, end: route.endTime || t('autoFleetGen.modeSelection.mixed.noEndTime') })}
                                </Typography>
                              )}
                            </Box>
                          </Stack>
                        </Paper>
                      );
                    })}
                  </Box>
                  
                  {selectedRouteIds.length > 0 && (
                    <Alert severity="success" sx={{ mt: 2 }}>
                      {t(
                        selectedRouteIds.length === 1
                          ? 'autoFleetGen.modeSelection.mixed.routesOneSelected'
                          : 'autoFleetGen.modeSelection.mixed.routesOtherSelected',
                        { total: selectedRouteIds.length }
                      )}
                    </Alert>
                  )}
                </>
              )}
            </Paper>

            {/* On-Demand Trip Source Selection */}
            <Paper elevation={1} sx={{ p: 2 }}>
              <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Icon d={ICON_FALLBACKS.Layers} size={20} /> {t('autoFleetGen.modeSelection.mixed.onDemandTitle')}
              </Typography>

              {tripLayers.length === 0 ? (
                <Alert severity="info" sx={{ mt: 1 }}>
                  {t('autoFleetGen.modeSelection.mixed.noTripLayers')}
                </Alert>
              ) : (
                <>
                  <FormControl fullWidth sx={{ mt: 2 }}>
                    <InputLabel>{t('autoFleetGen.modeSelection.mixed.selectTripLayer')}</InputLabel>
                    <Select
                      value={selectedLayerId}
                      onChange={handleMixedLayerChange}
                      label={t('autoFleetGen.modeSelection.mixed.selectTripLayer')}
                      MenuProps={{ sx: { zIndex: 2600 } }}
                    >
                      <MenuItem value="">
                        <em>{t('autoFleetGen.modeSelection.mixed.noneOption')}</em>
                      </MenuItem>
                      {tripLayers.map((layer) => (
                        <MenuItem key={layer.layerId} value={layer.layerId.toString()}>
                          {layer.layerName || `Layer ${layer.layerId}`}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {selectedLayerId && (() => {
                    const selectedLayer = layers?.find(l => l.layerId.toString() === selectedLayerId);
                    const layerSummary = selectedLayer ? getTripLayerSummary(selectedLayer) : null;
                    return layerSummary ? (
                      <Alert severity="info" sx={{ mt: 2 }}>
                        <Typography variant="body2">
                          <strong>{t('autoFleetGen.modeSelection.mixed.layerSummary', { count: layerSummary.tripCount })}</strong>
                          {layerSummary.tripsWithTime > 0 && t('autoFleetGen.modeSelection.mixed.layerSummaryWithTime', { value: layerSummary.tripsWithTime })}
                        </Typography>
                      </Alert>
                    ) : null;
                  })()}
                </>
              )}
            </Paper>

            {/* Validation Summary */}
            {selectedRouteIds.length === 0 && !selectedLayerId && (
              <Alert severity="warning">
                {t('autoFleetGen.modeSelection.mixed.selectAtLeastOne')}
              </Alert>
            )}

            {(selectedRouteIds.length > 0 || selectedLayerId) && (
              <Alert severity="success">
                <Typography variant="body2">
                  <strong>{t('autoFleetGen.modeSelection.mixed.readyToProceed')}</strong>
                  {selectedRouteIds.length > 0 && (
                    <span> {t(
                      selectedRouteIds.length === 1
                        ? 'autoFleetGen.modeSelection.mixed.routesOneSelected'
                        : 'autoFleetGen.modeSelection.mixed.routesOtherSelected',
                      { total: selectedRouteIds.length }
                    )}</span>
                  )}
                  {selectedRouteIds.length > 0 && selectedLayerId && t('autoFleetGen.modeSelection.mixed.andSeparator')}
                  {selectedLayerId && t('autoFleetGen.modeSelection.mixed.tripLayerSelected')}
                </Typography>
              </Alert>
            )}
          </Stack>
        </Box>
      )}
    </Box>
  );
};

export default ModeSelectionStep;

